import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { api, PinStats } from "../api";
import { warmthFor } from "../brand/warmth";
import { Wordmark } from "../components/Wordmark";
import { MapView, Pin } from "../components/MapView";

/** The hider's private view: how the seekers did. Gated on the hider token. */
export function Stats() {
  const { pinId = "" } = useParams();
  const [search] = useSearchParams();
  const token = search.get("token") ?? "";
  const [stats, setStats] = useState<PinStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError("This page needs the private link you got when you hid.");
      return;
    }
    api
      .stats(pinId, token)
      .then(setStats)
      .catch(() => setError("That link is not valid for this pin."));
  }, [pinId, token]);

  if (error) {
    return (
      <div className="page">
        <h1>Not available</h1>
        <p style={{ color: "var(--fog)" }}>{error}</p>
        <Link to="/">Back to the start</Link>
      </div>
    );
  }
  if (!stats) {
    return <div className="page" style={{ color: "var(--fog)" }}>Loading…</div>;
  }

  const pins: Pin[] = [
    {
      lat: stats.place.lat,
      lng: stats.place.lng,
      label: stats.place.name,
      kind: "target",
    },
    ...stats.guesses.map((g) => ({
      lat: g.lat,
      lng: g.lng,
      kind: (g.correct ? "found" : "guess") as "found" | "guess",
      color: warmthFor(g.distanceKm).color,
    })),
  ];

  const findRate =
    stats.attemptCount > 0
      ? Math.round((stats.foundCount / stats.attemptCount) * 100)
      : null;

  return (
    <div>
      <div className="topbar">
        <Wordmark size={20} />
        <Link to="/hide" className="btn btn-ghost" >
          Hide again
        </Link>
      </div>

      <div className="page">
        <div className="label">Your spot</div>
        <h1 style={{ fontSize: 32, margin: "10px 0 4px" }}>
          {stats.place.name}
        </h1>
        <p style={{ color: "var(--fog)", marginTop: 0 }}>
          {stats.place.countryName} · hidein.app/p/{stats.pinId}
        </p>

        <div
          style={{
            position: "relative",
            height: 340,
            border: "1px solid var(--line)",
            borderRadius: "var(--r-card)",
            overflow: "hidden",
            margin: "22px 0",
          }}
        >
          <MapView pins={pins} fitTo={pins.length > 1 ? pins : null} layerToggle />
        </div>
        <p className="clue-sub" style={{ marginTop: -12 }}>
          Moss is your spot and any guess that found it. Everything else is a
          miss, coloured by how close it landed.
        </p>

        <div style={{ marginTop: 24 }}>
          <div className="stat-row">
            <span>Attempts</span>
            <span className="num">{stats.attemptCount}</span>
          </div>
          <div className="stat-row">
            <span>Found you</span>
            <span className="num">
              {stats.foundCount}
              {findRate !== null && ` · ${findRate}%`}
            </span>
          </div>
          <div className="stat-row">
            <span>Average spend to find you</span>
            <span className="num">
              {stats.avgSpend === null ? "—" : `${stats.avgSpend} / 100`}
            </span>
          </div>
          <div className="stat-row">
            <span>Your average score</span>
            <span className="num" style={{ color: "var(--moss)" }}>
              {stats.avgHiderScore ?? "—"}
            </span>
          </div>
        </div>

        {stats.clueCounts.length > 0 && (
          <div style={{ marginTop: 28 }}>
            <div className="label" style={{ marginBottom: 8 }}>
              What they bought
            </div>
            {stats.clueCounts.map((clue) => (
              <div className="stat-row" key={clue.type}>
                <span>
                  {clue.icon} {clue.label}
                </span>
                <span className="num">{clue.count}×</span>
              </div>
            ))}
          </div>
        )}

        {stats.attemptCount === 0 && (
          <p style={{ color: "var(--fog)", marginTop: 26 }}>
            No hunts yet. Send the link to someone.
          </p>
        )}
      </div>
    </div>
  );
}
